import { useEffect, useId, useRef, useState } from 'react'
import type { DragEvent as ReactDragEvent } from 'react'
import { CircleAlert, FileUp, ShieldCheck } from 'lucide-react'
import { MAX_SSH_KEY_BYTES } from '@shared/constants'
import type { CreateSshKeyRequest, SshKeyDTO } from '@shared/types'
import { api, errorMessage, isApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import { Button, Input, Modal, Textarea } from '@/components/ui'
import PasswordField from '@/components/auth/PasswordField'

const MAX_KEY_KB = Math.floor(MAX_SSH_KEY_BYTES / 1024)

const KEY_PLACEHOLDER = `-----BEGIN OPENSSH PRIVATE KEY-----
b3BlbnNzaC1rZXktdjEAAAAABG5vbmUAAAAEbm9uZQAAAAAAAAABAAAAMwAAAAtzc2gt
…
-----END OPENSSH PRIVATE KEY-----`

const encoder = new TextEncoder()

interface FieldErrors {
  label?: string
  privateKey?: string
  passphrase?: string
}

export interface AddKeyDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (key: SshKeyDTO) => void
}

function validatePrivateKey(value: string): string | null {
  const text = value.trim()
  if (!text) return 'Paste your private key, or choose the key file.'
  if (/^(ssh-(rsa|ed25519|dss)|ecdsa-sha2-)/.test(text)) {
    return 'That is the public key. Argus needs the private half — the file without the .pub extension, e.g. ~/.ssh/id_ed25519.'
  }
  if (!text.startsWith('-----BEGIN')) {
    return 'A private key starts with a line like -----BEGIN OPENSSH PRIVATE KEY-----. Paste the whole file, including the BEGIN and END lines.'
  }
  if (!/-----END [^-]+-----\s*$/.test(text)) {
    return 'This key looks truncated — the closing -----END …----- line is missing.'
  }
  if (encoder.encode(text).length > MAX_SSH_KEY_BYTES) {
    return `That is larger than ${MAX_KEY_KB} KB, bigger than any real SSH key. Check that you pasted a single key file.`
  }
  return null
}

export default function AddKeyDialog({
  open,
  onClose,
  onCreated
}: AddKeyDialogProps): React.ReactElement {
  const formId = useId()
  const fileInput = useRef<HTMLInputElement>(null)

  const [label, setLabel] = useState('')
  const [privateKey, setPrivateKey] = useState('')
  const [passphrase, setPassphrase] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setLabel('')
    setPrivateKey('')
    setPassphrase('')
    setErrors({})
    setFormError(null)
    setDragging(false)
  }, [open])

  const readFile = async (file: File): Promise<void> => {
    if (file.size > MAX_SSH_KEY_BYTES) {
      setErrors((current) => ({
        ...current,
        privateKey: `${file.name} is larger than ${MAX_KEY_KB} KB, so it is not an SSH private key.`
      }))
      return
    }

    try {
      const text = await file.text()
      setPrivateKey(text)
      setErrors((current) => ({ ...current, privateKey: validatePrivateKey(text) ?? undefined }))
      // The filename is almost always the better label, and it is free.
      if (!label.trim()) setLabel(file.name.replace(/\.(pem|key|txt)$/i, ''))
    } catch {
      setErrors((current) => ({
        ...current,
        privateKey: 'That file could not be read. Open it and paste the text instead.'
      }))
    }
  }

  const onDrop = (event: ReactDragEvent<HTMLDivElement>): void => {
    event.preventDefault()
    setDragging(false)
    const file = event.dataTransfer.files[0]
    if (file) void readFile(file)
  }

  const submit = async (): Promise<void> => {
    const trimmedLabel = label.trim()
    const next: FieldErrors = {}
    if (!trimmedLabel) next.label = 'Give this key a name you will recognise, e.g. “Work laptop”.'
    const keyProblem = validatePrivateKey(privateKey)
    if (keyProblem) next.privateKey = keyProblem

    setErrors(next)
    setFormError(null)
    if (next.label || next.privateKey) return

    // A trailing newline is part of the PEM format and some parsers insist on it.
    const body: CreateSshKeyRequest = {
      label: trimmedLabel,
      privateKey: `${privateKey.trim()}\n`
    }
    if (passphrase) body.passphrase = passphrase

    setSaving(true)
    try {
      const created = await api.post<SshKeyDTO>('/ssh-keys', body)
      onCreated(created)
      onClose()
    } catch (cause) {
      if (isApiError(cause)) {
        const details = cause.details
        const fromDetails: FieldErrors = {
          label: details?.label?.[0],
          privateKey: details?.privateKey?.[0],
          passphrase: details?.passphrase?.[0]
        }
        if (fromDetails.label || fromDetails.privateKey || fromDetails.passphrase) {
          setErrors(fromDetails)
        } else if (cause.status === 400 || cause.status === 422) {
          // The server's parse failure is about the key material, so it belongs
          // on the field the user has to fix.
          if (/passphrase/i.test(cause.message)) setErrors({ passphrase: cause.message })
          else setErrors({ privateKey: cause.message })
        } else {
          setFormError(cause.message)
        }
      } else {
        setFormError(errorMessage(cause))
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!saving) onClose()
      }}
      title="Add an SSH key"
      description="Argus uses this key to reach your WP Engine installs over SSH."
      footer={
        <>
          <Button variant="secondary" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={formId} loading={saving}>
            Add key
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <Input
          label="Label"
          value={label}
          autoFocus
          autoComplete="off"
          placeholder="Work laptop"
          error={errors.label}
          hint="Only for you — it identifies the key when you assign it to a site."
          onChange={(event) => {
            setLabel(event.target.value)
            setErrors((current) => ({ ...current, label: undefined }))
          }}
        />

        <div>
          <div
            onDragOver={(event) => {
              event.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            className={cn(
              'rounded-control transition-shadow',
              dragging && 'ring-2 ring-accent ring-offset-2 ring-offset-surface'
            )}
          >
            <Textarea
              label="Private key"
              value={privateKey}
              spellCheck={false}
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              placeholder={KEY_PLACEHOLDER}
              error={errors.privateKey}
              className="h-48 resize-y font-mono text-xs leading-snug"
              onChange={(event) => {
                setPrivateKey(event.target.value)
                setErrors((current) => ({ ...current, privateKey: undefined }))
              }}
            />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              iconLeft={<FileUp />}
              onClick={() => fileInput.current?.click()}
            >
              Choose key file
            </Button>
            <p className="text-xs text-fg-muted">
              {dragging ? 'Drop the key file to load it.' : 'Or drop a .pem / .key file above.'}
            </p>
          </div>

          <input
            ref={fileInput}
            type="file"
            accept=".pem,.key,.txt,text/plain"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void readFile(file)
              // Cleared so re-picking the same file still fires a change event.
              event.target.value = ''
            }}
          />
        </div>

        <PasswordField
          label="Passphrase (optional)"
          value={passphrase}
          autoComplete="off"
          error={errors.passphrase}
          hint="Only if you set one when the key was generated. Stored encrypted, exactly like the key."
          onChange={(event) => {
            setPassphrase(event.target.value)
            setErrors((current) => ({ ...current, passphrase: undefined }))
          }}
        />

        <div className="flex gap-2.5 rounded-control border border-line bg-sunken/70 p-3">
          <ShieldCheck
            className="mt-0.5 size-4 shrink-0 text-ok-600 dark:text-ok-300"
            aria-hidden="true"
          />
          <p className="text-xs leading-relaxed text-fg-muted">
            The key is encrypted before it is stored and is never sent back to the browser — not even
            to you, so keep your own copy. Argus decrypts it in memory only for the moment it opens an
            SSH connection, and never writes it to a log.
          </p>
        </div>

        {formError ? (
          <div
            role="alert"
            className="flex gap-2.5 rounded-control border border-bad-500/40 bg-bad-100/60 px-3 py-2.5 dark:bg-bad-950/50"
          >
            <CircleAlert
              className="mt-0.5 size-4 shrink-0 text-bad-700 dark:text-bad-300"
              aria-hidden="true"
            />
            <div className="min-w-0 space-y-1 text-sm leading-relaxed">
              <p className="font-semibold text-bad-700 dark:text-bad-300">Key not saved</p>
              <p className="break-words text-fg-muted">{formError}</p>
            </div>
          </div>
        ) : null}
      </form>
    </Modal>
  )
}

export { AddKeyDialog }
